import Dimensions from "../models/Dimensions";
import { ImageModel } from "../models/ImageModel";
import Nullable from "../models/Nullable.types";

/**
 * Umbraco serialises content dates (dateStart, dateEnd, postDate) as a floating
 * wall-clock value with no timezone designator, e.g. "2026-09-10T10:00:00" meaning
 * 10am at the venue. Media dates by contrast carry a "Z" and really are UTC.
 *
 * Passing a floating value to `new Date()` makes the browser interpret it in the
 * VIEWER's timezone, so formatting it in any fixed zone afterwards shifts the
 * displayed time by the viewer's offset (a 10am Seattle event rendered in UTC
 * showed as 5pm). Rebuild the Date from the literal components instead, so the
 * wall-clock value survives verbatim and plain `format()` prints what the editor
 * typed regardless of where the visitor is.
 *
 * Values that DO carry an explicit offset ("Z" or "+HH:MM") are genuine instants
 * and are left to the standard parser.
 */
export function parseFloatingDate(value: string | Date | null | undefined): Date | null {
    // Some models type these fields as Date even though JSON.parse hands back a
    // string, so accept either rather than forcing casts at the call sites.
    if (value instanceof Date) {
        return isNaN(value.valueOf()) ? null : value;
    }
    if (typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    if (trimmed.length === 0) {
        return null;
    }
    if (hasTimezoneDesignator(trimmed)) {
        const absolute = new Date(trimmed);
        return isNaN(absolute.valueOf()) ? null : absolute;
    }

    // Expect "YYYY-MM-DD" optionally followed by "THH:MM[:SS[.fff]]".
    const datePart = splitOnce(trimmed, 'T');
    const ymd = datePart.head.split('-');
    if (ymd.length !== 3) {
        const fallback = new Date(trimmed);
        return isNaN(fallback.valueOf()) ? null : fallback;
    }

    const year = toInt(ymd[0]);
    const month = toInt(ymd[1]);
    const day = toInt(ymd[2]);
    if (year === null || month === null || day === null) {
        return null;
    }

    let hours = 0;
    let minutes = 0;
    let seconds = 0;
    let milliseconds = 0;
    if (datePart.tail.length > 0) {
        const hms = splitOnce(datePart.tail, '.');
        const clock = hms.head.split(':');
        hours = toInt(clock[0]) ?? 0;
        minutes = clock.length > 1 ? (toInt(clock[1]) ?? 0) : 0;
        seconds = clock.length > 2 ? (toInt(clock[2]) ?? 0) : 0;
        if (hms.tail.length > 0) {
            // Pad or trim the fraction to exactly three digits.
            const fraction = (hms.tail + '000').slice(0, 3);
            milliseconds = toInt(fraction) ?? 0;
        }
    }

    // Constructing from components uses the local timezone, which is exactly what
    // makes the wall-clock reading come back out unchanged.
    const result = new Date(year, month - 1, day, hours, minutes, seconds, milliseconds);
    return isNaN(result.valueOf()) ? null : result;
}

function hasTimezoneDesignator(value: string) {
    if (value.endsWith('Z') || value.endsWith('z')) {
        return true;
    }
    // An offset such as "+02:00" or "-07:00" only ever appears in the time portion,
    // so ignore the "-" separators inside the leading date.
    const timeStart = value.indexOf('T');
    if (timeStart < 0) {
        return false;
    }
    const timePart = value.slice(timeStart + 1);
    return timePart.includes('+') || timePart.includes('-');
}

function splitOnce(value: string, separator: string) {
    const index = value.indexOf(separator);
    if (index < 0) {
        return { head: value, tail: '' };
    }
    return { head: value.slice(0, index), tail: value.slice(index + separator.length) };
}

function toInt(value: string | undefined) {
    if (value === undefined || value.length === 0) {
        return null;
    }
    let total = 0;
    for (let i = 0; i < value.length; i++) {
        const digit = value.charCodeAt(i) - 48;
        if (digit < 0 || digit > 9) {
            return null;
        }
        total = total * 10 + digit;
    }
    return total;
}

export function getBaseURL() {
    //if (window.location.hostname.indexOf('localhost') > -1) {
    if (process.env.REACT_APP_DEV === 'true') {
        return process.env.REACT_APP_BASE_URL;
    } else {
        return window.location.origin;
    }
}

export const paginateItems = (items: any[], currentPage: number, pageSize: number) => {
  const offset = pageSize * (currentPage - 1);
  return items.slice(offset, pageSize * currentPage);
}

export interface Json {
  [x: string]: string | number | boolean | Date | Json | JsonArray | undefined;
}
export interface JsonArray extends Array<string | number | boolean | Date | Json | JsonArray> { }
export async function fetchAPI<T>(action: string, params: Json = {}, preview: boolean = false) {
  var query = '';
  var keys = Object.keys(params);
  if (keys.length) {
      query = '?';
      keys.forEach((key, i) => {
          if (i > 0) {
              query += '&';
          }
          query += `${key}=${params[key] + ''}`
      })
  }
  const dateRegex = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)(?:Z|(\+|-)([\d|:]*))?$/;
  const parseDates = (data: any) : any => {
      if (!data) {
          return data;
      }
      if (Array.isArray(data)) {
          return data.map(item => {
              return parseDates(item);
          })
      }
      else if (typeof (data) === 'object') {
          var keys = Object.keys(data);
          var result : Json = {};
          keys.forEach(key => {
              result[key] = parseDates(data[key]);
          });
          return result;
      }
      else if (typeof (data) === 'string') {
          var match = dateRegex.exec(data);
          if (match) {
              return new Date(data);
          }
      }
      return data;
  }

  const requestSettings = {
      method: 'GET',
      headers: {
          'Content-Type': 'application/json',
          //'Api-Key': process.env.UMBRACO_API_KEY + '',
          //'Preview-Key': ''
      }
  };

  const res = await fetch(getBaseURL() + action + query, requestSettings);
  const json = await res.json();
  if (json.errors) {
      console.error(json.errors);
      throw new Error('Failed to fetch API');
  }
  const results = parseDates(json);
  return results as T;
}

export function getAbsoluteMediaUrl(url: string): string {
    if (!url) {
        return '';
    }
    if (url.indexOf('//') > -1) {
        return url;
    }
    return process.env.NEXT_PUBLIC_MEDIA_DOMAIN + url;
}

export function getMediaDimensions(model: ImageModel, cropAlias?: string, width?: number, height?: number): Nullable<Dimensions> {
    if (width !== undefined || height !== undefined) {
        if (width === 0 && height === 0) {
            return {
                width: model.width,
                height: model.height
            }
        }
        if (!height) {
            height = model.height * ((width || 0) / model.width);
        }
        else if (!width) {
            width = model.width * (height / model.height);
        }
        return {
            width: width || 0,
            height
        };
    }
    if (cropAlias) {
        var crop = model.crops && model.crops.crops ? model.crops.crops.find(x => x.alias.toLowerCase() === cropAlias.toLowerCase()) : null;
        if (!crop) {
            return {
                width: model.width,
                height: model.height
            };
        }
        return {
            width: crop.width,
            height: crop.height
        };
    }
    return null;
}

export function getCropUrl(model: ImageModel, cropAlias?: string, width?: number, height?: number): string {
    if (!model) {
        return '';
    }
    //var url = getAbsoluteMediaUrl(model.url);
    var url = model.url;
    if (width || height) {
        const dimensions = getMediaDimensions(model, cropAlias, width, height);
        if (dimensions) {
            return url + '?rnd=' + model.updateDate.getTime() + '&width=' + dimensions.width + '&height=' + dimensions.height;
        }
    }
    if (!model.crops || !cropAlias) {
        return url;
    }
    var crop = model.crops.crops.find(x => x.alias.toLowerCase() === cropAlias.toLowerCase());
    if (!crop) {
        return url;
    }
    var query = '';
    if (crop.coordinates) {
        if (crop.coordinates.left !== undefined) {
            query = '?rxy=' + crop.coordinates.left + ',' + crop.coordinates.top;
        } else {
            query = "?cc=" + crop.coordinates.x1 + ',' + crop.coordinates.y1 + ',' + crop.coordinates.x2 + ',' + crop.coordinates.y2;
        }
    }
    else if (model.crops.focalPoint && model.crops.focalPoint.left !== undefined) {
        query = '?rxy=' + model.crops.focalPoint.left + ',' + model.crops.focalPoint.top;
    }
    query += (query ? '&' : '?') + '&rnd=' + model.updateDate.getTime() + '&width=' + crop.width + '&height=' + crop.height;
    return (process.env.REACT_APP_DEV === 'true' ? process.env.REACT_APP_BASE_URL : '') +  url + query;
}

export function checkDateIfSame(date1: Date, date2: Date) {
    const date = new Date(date1);
    const dateToCompare = new Date(date2);
    let result = false;
    //console.log('date.getYear', date.getFullYear());
    if (date.getFullYear() < 2000) {
        result = true;
    }
    else {
        result = date.getFullYear() === dateToCompare.getFullYear() && date.getMonth() === dateToCompare.getMonth() && date.getDay() === dateToCompare.getDay();
    }
    return result;
}

export function filterOnlyUnique(value: any, index: number, array: any) {
    return array.indexOf(value) === index;
}

export function getSearchMessage(searchTerm: string, totalRecords: number, noResultMessage: string, resultMessage: string) {
  let message = '';
  if (searchTerm.length > 0) {
    message = noResultMessage;
    if (totalRecords > 0) {
      message = resultMessage.replace('[count]', totalRecords.toString());
    }
    message = message.replace('[searchTerm]', searchTerm);
  }

  return message;
}

export function getServerTimezoneDate(date: Date) {
    return new Date(date.valueOf() + date.getTimezoneOffset() * 60 * 1000)
}